import './BroadcastMessage.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BigNumber from 'bignumber.js';

import {
  BottomBar,
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  ScrollView,
  Switch,
} from '$uikit';
import { useTranslator } from '$hooks';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import { imActions, imBroadcastReceiversCountSelector, imSelector } from '$store/im';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { IcCloud28, IcGallery28, IcVideo28 } from '$assets';
import { formatInputAmount } from '$utils/number';
import { isBackNavigation } from '$navigation/router';
import { goTo } from '$navigation/helper';
import { CLOUD, IM } from '$shared/constants/pages';
import { mainSelector } from '$store/main';
import { ATTACHMENTS_COUNT_LIMIT, formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { cloudActions } from '$store/cloud';
import { attachPhotosSelector } from '$store/attach';
import { RootState } from '$store/rootReducer';
import { useRoute } from 'react-router5';
import { toastActions } from '$store/toast';

export const BroadcastMessage: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const isBack = isBackNavigation();

  const { route } = useRoute();
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const { tonRate, broadcast } = useSelector(imSelector);
  const receiversCount = useSelector(imBroadcastReceiversCountSelector);
  const { canBroadcastMessages, feePercent, mVerified, isBlackFriday2025Enabled } =
    useSelector(mainSelector);

  const price = broadcast.price;
  const priceWithoutDiscount = broadcast.priceWithoutDiscount;
  const currentCurrency = CurrencyEnum.EUR as CurrencyEnum;

  useEffect(() => {
    dispatch(imActions.loadTonRate());
  }, []);

  useEffect(() => {
    if (!mVerified) {
      goTo(IM);
    }
  }, [mVerified]);

  const pickerId = 'broadcast_message';

  const mediaAttachments = useSelector((state: RootState) =>
    attachPhotosSelector(state, pickerId),
  );

  const mediaCount = mediaAttachments.length;
  const attachmentsLimit = ATTACHMENTS_COUNT_LIMIT;

  const handlePhotoChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handlePriceChange = useCallback(({ target }) => {
    const raw = formatInputAmount(target.value, 2);

    const priceV = parseFloat(raw || '0');
    const priceWithoutDiscountV = parseFloat(priceWithoutDiscount || '0');

    dispatch(imActions.setBroadcastPrice(raw));

    if (priceV > priceWithoutDiscountV) {
      dispatch(imActions.setBroadcastPriceWithoutDiscount(raw));
    }
  }, []);

  const handlePriceWithoutDiscountChange = useCallback(({ target }) => {
    dispatch(
      imActions.setBroadcastPriceWithoutDiscount(formatInputAmount(target.value, 2)),
    );
  }, []);

  const handleTextChange = useCallback(({ target }) => {
    dispatch(imActions.setBroadcastMessageText(target.value));
  }, []);

  const priceTip = useMemo(() => {
    if (+price < 0.1 || !price) {
      return t('create_invoice_price_description', {
        amount: formatCurrency(0.1, currentCurrency),
      });
    }

    const resultPrice = new BigNumber(price).multipliedBy(1 - feePercent).toPrecision();
    return t('create_invoice_price_fee_tip', {
      amount: formatCurrency(+resultPrice, currentCurrency),
      fee: feePercent * 100,
    });
  }, [t, price, tonRate, feePercent]);

  const handlePaid = useCallback(() => {
    dispatch(imActions.setBroadcastPaid(!broadcast.isPaid));
  }, [broadcast.isPaid]);

  const handleRollbackPrevious = useCallback(() => {
    dispatch(imActions.setBroadcastRollbackPrevious(!broadcast.rollbackPrevious));
  }, [broadcast.rollbackPrevious]);

  const handleSkipUpToTopDialogs = useCallback(() => {
    dispatch(imActions.setBroadcastSkipUpToTopDialogs(!broadcast.skipUpToTopDialogs));
  }, [broadcast.skipUpToTopDialogs]);

  const handleSend = useCallback(() => {
    dispatch(imActions.sendBroadcastMessage());
  }, []);

  const handleOnClickCheckAttachmentsLimit = (e) => {
    if (mediaCount >= attachmentsLimit) {
      dispatch(
        toastActions.setToastFail(
          t('broadcast_message_limit_error', { attachmentsLimit }),
        ),
      );
      return e.preventDefault();
    }
  };

  const priceMaxLimit = currentCurrency === CurrencyEnum.PLACE ? 5000000 : 1000;
  const priceWithoutDiscountMaxLimit = priceMaxLimit * 2;

  const formPrice = Math.max(parseFloat(price || '0'), 0);
  const formPriceWithoutDiscount = Math.max(
    formPrice,
    parseFloat(priceWithoutDiscount || '0'),
  );
  const savings = formPriceWithoutDiscount - formPrice;

  function renderContent() {
    return (
      <>
        <ScrollView>
          <Form>
            <FormItem>
              <List>
                <ListItem
                  onClick={handlePaid}
                  after={<Switch checked={broadcast.isPaid} />}
                  disableHighlight
                >
                  {t('welcome_message_paid')}
                </ListItem>
              </List>
            </FormItem>
            {broadcast.isPaid &&
              (isBlackFriday2025Enabled ? (
                <>
                  <FormItem title={t('create_invoice_price_without_discount_label')}>
                    <Input
                      type="price"
                      currency={currentCurrency}
                      placeholder={t('create_invoice_price_without_discount_placeholder')}
                      value={priceWithoutDiscount}
                      onChange={handlePriceWithoutDiscountChange}
                      minPrice={0}
                      maxPrice={priceWithoutDiscountMaxLimit}
                    />
                  </FormItem>
                  <FormItem
                    title={t('create_invoice_price_with_discount_label')}
                    description={t('create_invoice_price_with_discount_desc', {
                      amount: formatCurrency(formPrice * 0.8, currentCurrency),
                      cashback: formatCurrency(
                        Math.min(savings, formPrice * 0.2),
                        currentCurrency,
                      ),
                      recommended: formatCurrency(
                        formPriceWithoutDiscount * 0.7,
                        currentCurrency,
                      ),
                    })}
                  >
                    <Input
                      type="price"
                      currency={currentCurrency}
                      placeholder={t('create_invoice_price_with_discount_placeholder')}
                      value={price}
                      onChange={handlePriceChange}
                      minPrice={0}
                      maxPrice={priceMaxLimit}
                    />
                  </FormItem>
                </>
              ) : (
                <FormItem title={t('create_invoice_price_label')} description={priceTip}>
                  <Input
                    placeholder={t('create_invoice_price_placeholder')}
                    value={broadcast.price}
                    onChange={handlePriceChange}
                    type="price"
                    currency={currentCurrency}
                  />
                </FormItem>
              ))}
            <FormItem
              title={t('create_invoice_message_label')}
              description={
                broadcast.isPaid ? t('create_invoice_message_input_tip') : null
              }
            >
              <Input
                multiline
                placeholder={t('create_invoice_message_placeholder')}
                value={broadcast.text}
                onChange={handleTextChange}
              />
            </FormItem>
            <FormItem>
              <List>
                <ListItem
                  onClick={handleRollbackPrevious}
                  after={<Switch checked={broadcast.rollbackPrevious} />}
                  disableHighlight
                  description={t(
                    'broadcast_message_option_rollback_previous_description',
                  )}
                >
                  {t('broadcast_message_option_rollback_previous')}
                </ListItem>
              </List>
            </FormItem>
            {/*<FormItem>*/}
            {/*  <UsersList>*/}
            {/*    <ListItem*/}
            {/*      onClick={handleSkipUpToTopDialogs}*/}
            {/*      after={<Switch checked={!broadcast.skipUpToTopDialogs} />}*/}
            {/*      disableHighlight*/}
            {/*    >*/}
            {/*      {t('broadcast_message_option_upp_to_top_dialogs')}*/}
            {/*    </ListItem>*/}
            {/*  </UsersList>*/}
            {/*</FormItem>*/}
            <FormItem
              title={t('create_invoice_content_label')}
              description={t('create_invoice_content_description')}
            >
              <div className="BroadcastMessage__attach_wrap">
                <AttachPickerManager ref={attachManager} pickerId={pickerId} />
              </div>
              <div className="BroadcastMessage__select_button_wrap">
                <div className="CreateInvoice__select_button">
                  <Button
                    size="small"
                    mode="secondary"
                    icon={<IcCloud28 />}
                    onClick={() => {
                      if (mediaCount >= attachmentsLimit) {
                        dispatch(
                          toastActions.setToastFail(
                            t('broadcast_message_limit_error', { attachmentsLimit }),
                          ),
                        );
                        return;
                      }
                      // dispatch(
                      //   cloudActions.setSavedContentActions([
                      //     attachActions.pushPhotos({
                      //       pickerId,
                      //       photos: mediaAttachments,
                      //     }),
                      //   ]),
                      // );
                      dispatch(
                        cloudActions.setAttachParams({
                          pickerId,
                          routeName: route.name,
                          routeParams: route.params,
                        }),
                      );
                      goTo(
                        CLOUD,
                        {},
                        {
                          limit: attachmentsLimit - mediaCount,
                        },
                      );
                    }}
                  >
                    {t('new_post_attach_cloud')}
                  </Button>
                </div>
                <div className="BroadcastMessage__select_button">
                  <Button size="small" mode="secondary" icon={<IcGallery28 />}>
                    {t('new_post_attach_photo')}
                  </Button>
                  <input
                    key={photoInputKey}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    onClick={handleOnClickCheckAttachmentsLimit}
                    multiple
                  />
                </div>
                <div className="BroadcastMessage__select_button">
                  <Button size="small" mode="secondary" icon={<IcVideo28 />}>
                    {t('new_post_attach_video')}
                  </Button>
                  <input
                    key={videoInputKey}
                    type="file"
                    accept="video/mp4,video/quicktime"
                    onChange={handleVideoChange}
                    onClick={handleOnClickCheckAttachmentsLimit}
                  />
                </div>
              </div>
            </FormItem>
          </Form>
        </ScrollView>
        <BottomBar>
          <Button disabled={!receiversCount} onClick={handleSend} stretched>
            {t('broadcast_send_button', { count: receiversCount })}
          </Button>
        </BottomBar>
      </>
    );
  }

  if (!canBroadcastMessages) {
    goTo(IM);
    return null;
  }

  return (
    <>
      <Header showBackButton>
        <div className="Broadcast__headerWrap">
          <div className="Broadcast__headerWrap__title">{t('broadcast_title')}</div>
          <div className="Broadcast__headerWrap__subtitle">
            {t('broadcast_subtitle', { count: receiversCount })}
          </div>
        </div>
      </Header>
      {renderContent()}
    </>
  );
};
